import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDamageClaim, damageClaimStatusBadge } from '../../services/disputeService';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

export default function ChargedClaimDetail() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDamageClaim(claimId)
      .then(setClaim)
      .catch((err) => toast.error(err.message || 'Could not load claim'))
      .finally(() => setLoading(false));
  }, [claimId]);

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="p-10 text-center text-muted">Loading...</div>;
  if (!claim) return <div className="p-10 text-center text-muted">Claim not found</div>;

  // Riders shouldn't see claims still in admin review (not yet actionned against them)
  if (!['approved_paid', 'rejected', 'awaiting_charge', 'charge_failed'].includes(claim.status)) {
    return (
      <div className="max-w-[700px] mx-auto p-6">
        <div className="p-10 text-center text-muted">
          This claim is under review and not yet visible.
        </div>
      </div>
    );
  }

  const badge = damageClaimStatusBadge(claim.status);

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/rider/claims')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← All claims</button>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h1 className="font-serif m-0">{claim.referenceNumber}</h1>
          <div className="text-[0.78rem] text-muted mt-1 capitalize">
            {claim.type} claim · Submitted {fmtDate(claim.submittedAt)}
          </div>
        </div>
        <span className="px-3 py-1 rounded-full text-[0.78rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
          {badge.label}
        </span>
      </div>

      {/* Outcome banner */}
      {claim.status === 'approved_paid' && (
        <div className="p-4 rounded-2xl mb-4" style={{ background: '#fee2e2', border: '1px solid #fecaca' }}>
          <div className="font-bold mb-1" style={{ color: '#991b1b' }}>
            You were charged ${claim.approvedAmount?.toFixed(2)}
          </div>
          <div className="text-[0.85rem]" style={{ color: '#991b1b' }}>
            Charged on {fmtDate(claim.riderChargedAt)} to your saved payment method.
          </div>
        </div>
      )}

      {claim.status === 'rejected' && (
        <div className="p-4 rounded-2xl mb-4" style={{ background: 'rgba(45,212,168,0.1)', border: '1px solid #2dd4a8' }}>
          <div className="font-bold mb-1" style={{ color: '#059669' }}>
            Claim rejected — no charge
          </div>
          <div className="text-[0.85rem]" style={{ color: '#059669' }}>
            Reservo admin rejected this claim. You were not charged.
          </div>
        </div>
      )}

      {claim.status === 'awaiting_charge' && (
        <div className="p-4 rounded-2xl mb-4" style={{ background: '#fef3c7', border: '1px solid #fde68a' }}>
          <div className="font-bold mb-1" style={{ color: '#92400e' }}>
            Pending charge: ${claim.approvedAmount?.toFixed(2)}
          </div>
          <div className="text-[0.85rem]" style={{ color: '#92400e' }}>
            This claim was approved and will be charged to your saved payment method shortly.
          </div>
        </div>
      )}

      {claim.status === 'charge_failed' && (
        <div className="p-4 rounded-2xl mb-4" style={{ background: '#fed7aa', border: '1px solid #fdba74' }}>
          <div className="font-bold mb-1" style={{ color: '#9a3412' }}>
            Charge failed
          </div>
          <div className="text-[0.85rem]" style={{ color: '#9a3412' }}>
            We couldn't charge your payment method. Please update your card details. Reservo admin
            will retry the charge.
          </div>
        </div>
      )}

      {/* Trip details */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
        <div className="text-[0.78rem] text-muted font-semibold uppercase mb-1.5">
          Trip details
        </div>
        <div className="text-[0.85rem] text-body">
          <div>Trip reference: <strong>{claim.bookingReferenceNumber || claim.bookingId?.substring(0, 8)}</strong></div>
          <div>Driver: <strong>{claim.driverName || '—'}</strong></div>
        </div>
      </div>

      {/* Driver's description */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
        <div className="text-[0.78rem] text-muted font-semibold uppercase mb-1.5">
          Driver's description
        </div>
        <div className="text-[0.9rem] text-brand-dark whitespace-pre-wrap">{claim.description}</div>
      </div>

      {/* Photos */}
      {claim.photos?.length > 0 && (
        <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
          <div className="text-[0.78rem] text-muted font-semibold uppercase mb-2">
            Driver's photos ({claim.photos.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {claim.photos.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt={`Damage ${i + 1}`}
                  className="w-[100px] h-[100px] object-cover rounded-lg border border-slate-200" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Admin notes (if any) */}
      {claim.adminNotes && (
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 mb-4">
          <div className="text-[0.78rem] text-muted font-semibold uppercase mb-1.5">
            Reservo admin notes
          </div>
          <div className="text-[0.9rem] text-body italic">"{claim.adminNotes}"</div>
        </div>
      )}

      {/* Dispute path */}
      <div className="p-4 rounded-2xl" style={{ background: '#fef3c7', border: '1px solid #fde68a' }}>
        <div className="font-semibold mb-1.5" style={{ color: '#92400e' }}>
          Disagree with this claim?
        </div>
        <p className="text-[0.85rem] mb-2.5 leading-normal" style={{ color: '#92400e' }}>
          If you believe this charge is incorrect or unfair, you can submit a formal complaint
          to Reservo for review. Our team will investigate and respond within 7 days.
        </p>
        <button onClick={() => navigate('/rider/complaint/new', {
          state: {
            bookingId: claim.bookingId,
            bookingReferenceNumber: claim.bookingReferenceNumber,
            againstDriverId: claim.driverId,
          },
        })} className="px-3.5 py-2 rounded-lg bg-white cursor-pointer font-semibold text-[0.85rem]"
          style={{ border: '1.5px solid #92400e', color: '#92400e' }}>Submit a complaint</button>
      </div>
    <BackButtonBottom to="/rider/claims" label="All claims" />
    </div>
  );
}